import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * THE DESIGNER'S PROCESSING DONUT — Jay Jung, 2026-10-01: "Designers see their review status only
 * once a month in the monthly report. That is too late to act on. A chart on the Welcome screen
 * lets each designer see their own backlog every day."
 *
 * THREE PARTS OF ONE WHOLE, which is the one job a donut does well: Done + In review + Waiting
 * adds up to Received, and the hole carries the processing rate rather than being empty decoration.
 * Jay asked for a donut and the data suits it — three slices, one total.
 *
 * THE COLOURS WERE MEASURED, NOT PICKED BY EYE. #059669 · #3B82F6 · #DC2626 pass all five checks of
 * the palette validator in BOTH light and dark against each surface, with no warning in either:
 * worst CVD pair ΔE 8.6 deutan, worst normal-vision pair 24.0, every slice over 3:1 on the surface.
 * The obvious reading — green, amber, red — was tried FIRST and failed: amber #B45309 against red
 * #B91C1C is ΔE 9.1 in NORMAL vision, under the floor of 15, which is the pair this portal already
 * has to hatch apart on the ticket charts.
 *
 * IDENTITY IS NEVER COLOUR ALONE. Every slice carries a `<title>` for hover, the legend prints each
 * name WITH its figure, and a 2px gap in the surface colour separates the arcs — so the chart still
 * reads in greyscale, in print and in forced colours.
 */

/* Light-to-dark is deliberate within the hue, and the order is the reading order: finished, in
   flight, overdue. A filter cannot repaint these — each belongs to a state, not to a rank. */
/* TWO KEYS, and they are not the same word. `key` is the field the server's payload carries;
   `state` is what the Sample Tracker's own filter is keyed on (`REVIEW_STATES` in
   15_SampleTracker.jsx). They differ on one part — inReview against in_review — and sending the
   payload's name in the link produced a URL the tracker simply ignored: no filter, no chip, and a
   list of 322 rows under a slice that said 27. Found by driving all three, not by reading. */
const PARTS = [
  /* The label is a dictionary key (xCharts.donut.*), read where it is drawn so a language switch
     redraws it — the `ko` flag the parent passes only knew two languages. */
  { key: 'done', state: 'done', color: '#059669', labelKey: 'xCharts.donut.done' },
  { key: 'inReview', state: 'in_review', color: '#3B82F6', labelKey: 'xCharts.donut.inReview' },
  { key: 'waiting', state: 'waiting', color: '#DC2626', labelKey: 'xCharts.donut.waiting' },
];

const R = 58;
const STROKE = 20;
const C = 2 * Math.PI * R;
/* A 2px gap in the surface between arcs, the same spacer the stacked columns on the ticket report
   use. Without it two slices of similar size read as one. */
const GAP = 2;

// `ko` is still accepted from the caller but no longer read: the strings follow the portal's language.
export default function DesignerDonut({ data, viewingAll, picName, siblings = 1 }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  if (!data) return null;
  const { received, days, rate } = data;
  const parts = PARTS.map((p) => ({ ...p, value: Number(data[p.key] || 0) }));

  /* NOTHING TO DRAW IS SAID, NOT DRAWN AS A ZERO RING. An empty donut reads as "all of it is in one
     state"; the sentence says which of the two it is. */
  if (!received) {
    return (
      <section className="dd-wrap">
        <h3 className="dd-h">{t('xCharts.donut.title')}</h3>
        <p className="dd-empty">{t('xCharts.donut.empty', { days })}</p>
      </section>
    );
  }

  /* EVERY PART OPENS ITS OWN LIST — Max, 2026-10-04. The slice and the legend row go to the same
     place, because they are the same fact drawn twice and a reader who clicks the one nearest their
     cursor should not land somewhere else.
     IT CARRIES THE WINDOW IT COUNTED WITH (`reviewDays`), the same way the Sales OP cards pass
     `lateDays` on: the rule lives on the server, and the thing that was clicked hands it over
     rather than the tracker guessing. The designer rides along when one person is in view, so the
     list is the slice rather than the slice of everybody. */
  const open = (state) => {
    const q = new URLSearchParams({ tab: 'sample', review: state, reviewDays: String(days) });
    /* Every designer the server counted, appended one by one — on one person's own screen that is
       one name, on a manager's it is the team without the external designers the query excludes.
       The list is then the slice rather than the slice of everybody. */
    (data.designers || []).forEach((n) => q.append('designer', n));
    navigate(`/development/sample-tracker?${q.toString()}`);
  };

  let offset = 0;
  const arcs = parts.filter((p) => p.value > 0).map((p) => {
    const len = (p.value / received) * C;
    /* The gap is taken off the arc, never added to the circumference, or the slices would stop
       adding up to the ring they are parts of. A slice too small to carry a gap keeps its own
       length — a hairline is still the truth. */
    const draw = Math.max(len - GAP, 0.5);
    const arc = { ...p, dash: `${draw} ${C - draw}`, offset: -offset };
    offset += len;
    return arc;
  });

  return (
    <section className="dd-wrap">
      <h3 className="dd-h">
        {t('xCharts.donut.title')}
        <span className="dd-sub">
          {t('xCharts.donut.lastDays', { days })}
          {viewingAll ? ` · ${t('xCharts.donut.everyone')}` : (picName ? ` · ${picName}` : '')}
        </span>
      </h3>

      {/* THE GRID IS ITS OWN BOX, with the heading OUTSIDE it. While the heading was a grid item
          spanning 1/-1 every track held something, so `auto-fit` collapsed none of them and this
          card came out 312px beside cards of 394. Measured: five tracks here against four there. */}
      <div className="dd-grid">
      <div className="dd-body">
        <svg className="dd-svg" viewBox="0 0 160 160" role="img"
          aria-label={t('xCharts.donut.aria', { done: data.done, received, days })}>
          {/* The ring the slices sit in, so a chart with one dominant slice still reads as a whole
              rather than as a lone arc floating in space. */}
          <circle cx="80" cy="80" r={R} fill="none" strokeWidth={STROKE} className="dd-track" />
          {arcs.map((a) => (
            <circle
              key={a.key}
              cx="80" cy="80" r={R} fill="none"
              stroke={a.color} strokeWidth={STROKE}
              strokeDasharray={a.dash} strokeDashoffset={a.offset}
              transform="rotate(-90 80 80)"
              className="dd-arc"
              onClick={() => open(a.state)}
              role="button" tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(a.state); } }}
            >
              {/* Hover, which an SVG chart gets by default — and the figure, so the number is
                  reachable without reading the legend. */}
              <title>{`${t(a.labelKey)}: ${a.value} / ${received}`}</title>
            </circle>
          ))}
          {/* THE HOLE EARNS ITSELF — Max, 2026-10-04: the processing rate goes in the middle. It is
              Done / Received, computed on the server so this and the monthly report cannot drift. */}
          <text x="80" y="76" className="dd-rate" textAnchor="middle">{rate}%</text>
          <text x="80" y="94" className="dd-ratelab" textAnchor="middle">
            {t('xCharts.donut.processed')}
          </text>
        </svg>

        {/* THE LEGEND CARRIES EVERY NAME AND EVERY FIGURE. Three browns on the ticket report taught
            this: colour alone is not identity, and in print or forced colours it is nothing. */}
        <ul className="dd-legend">
          {parts.map((p) => (
            <li key={p.key}>
              {/* A BUTTON, not a div with a handler: it is the only way to open this list, so it has
                  to be reachable by keyboard. A part with nothing in it is drawn plain — a control
                  that always lands on an empty list should not look like a control. */}
              {p.value > 0 ? (
                <button type="button" className="dd-row" onClick={() => open(p.state)}
                  title={t('xCharts.donut.openList')}>
                  <span className="dd-dot" style={{ background: p.color }} />
                  <span className="dd-name">{t(p.labelKey)}</span>
                  <span className="dd-val">{p.value}</span>
                </button>
              ) : (
                <span className="dd-row dd-row-off">
                  <span className="dd-dot" style={{ background: p.color }} />
                  <span className="dd-name">{t(p.labelKey)}</span>
                  <span className="dd-val">{p.value}</span>
                </span>
              )}
            </li>
          ))}
          <li className="dd-total">
            <span className="dd-row dd-row-off">
              <span className="dd-name">{t('xCharts.donut.received')}</span>
              <span className="dd-val">{received}</span>
            </span>
          </li>
        </ul>
      </div>
      {/* ONE EMPTY TRACK-FILLER PER CARD IN THE ROW BELOW — the only way to make two `auto-fit`
          grids agree. The template alone is not enough: auto-fit COLLAPSES the tracks that hold
          nothing, so at 1920px the four cards below sat in four tracks of 394px while this grid,
          holding one item, kept all five its width allowed and drew a 312px card. Measured, both
          before and after. They carry nothing and are hidden from assistive tech. */}
      {Array.from({ length: Math.max(0, siblings - 1) }, (_, i) => (
        <div key={i} className="dd-filler" aria-hidden="true" />
      ))}
      </div>
    </section>
  );
}
